import { useEffect } from 'react';

import { ModalTypes, ModalProps } from 'application/providers/ModalProvider';
import AnalyticsService, {
  EventNameAMP,
  PointParamNameAMP,
  ParamNameAMP,
  CalledFromValues,
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
  EventNameGTM,
} from 'application/services/AnalyticsService';

import { CurrentPointOnboardingType, DeviceTypesInFlow, SelectPlayerFlowType } from '../types';
import { StreamBindSuccessModalType } from '../StreamBindSuccessModal/types';

interface Props {
  flowType?: SelectPlayerFlowType;
  pathname: string;
  currentPoint: CurrentPointOnboardingType;
  redirectToPointsPage: () => void;
  redirectToPoint: () => void;
  redirectToStreamsPage: () => void;
  redirectToVideoStreamsPage: () => void;
  close: (count?: number) => void;
  replaceModal: <T extends ModalTypes>(
    type: T,
    modalState: Omit<ModalProps[T], 'close' | 'open' | 'openModal' | 'replaceModal'>,
    onModalCloseCallback?: () => void
  ) => void;
  clearCurrentPoint: () => void;
  setFlowType: (flowType: SelectPlayerFlowType) => void;
}

interface UseDeviceBindSuccessModalViewModel {
  addressHasStream: boolean;
  addressHasVideoStream: boolean;
  showWebPlayerButton: boolean;
  onOpenWebPlayerClick: () => void;
  onChangeStreamClick: () => void;
  onChangeVideoStreamClick: () => void;
  onClose: () => void;
  onBack: () => void;
}

const useDeviceBindSuccessModalViewModel = ({
  flowType,
  currentPoint,
  replaceModal,
  close,
  clearCurrentPoint,
  redirectToPoint,
  redirectToStreamsPage,
  redirectToVideoStreamsPage,
  setFlowType,
}: Props): UseDeviceBindSuccessModalViewModel => {
  const showWebPlayerButton =
    flowType !== SelectPlayerFlowType.BIND_STREAM &&
    currentPoint.device?.type === DeviceTypesInFlow.WEBPLAYER &&
    Boolean(currentPoint?.isEnabled);

  const addressHasStream =
    Boolean(currentPoint.stream?.id) ||
    flowType === SelectPlayerFlowType.CREATE_DEMO_POINT ||
    flowType === SelectPlayerFlowType.CHANGE_DEMO_DEVICE;

  const addressHasVideoStream = Boolean(currentPoint.videoStream);

  const onClose = () => {
    if (flowType === SelectPlayerFlowType.CREATE_DEMO_POINT || currentPoint?.isVideoDemo) {
      redirectToPoint();
    }

    clearCurrentPoint();
    close();
  };

  const onBack = () => {
    clearCurrentPoint();
    close();
  };

  const onOpenWebPlayerClick = () => {
    AnalyticsService.event(EventNameAMP.OPEN_WEBPLAYER_PRESSED, {
      [PointParamNameAMP.POINT_ID]: currentPoint?.id,
      [ParamNameAMP.CALLED_FROM]: CalledFromValues.STREAM_BC_SUCCESS,
    });

    if (currentPoint?.isEnabled === false) {
      replaceModal(ModalTypes.PAYMENT_REQUIREMENT, {});
    } else {
      setTimeout(() => {
        window.open(currentPoint.device?.requiredVersionUrl);
      }, 100);

      onClose();
    }
  };

  const onChangeStreamClick = () => {
    AnalyticsService.event(EventNameAMP.ADDRESS_STREAM_ADD_CLICKED, {
      [ParamNameAMP.TYPE]: 'success bind device modal',
    });

    setFlowType(SelectPlayerFlowType.FULL_ONBOARDING);
    redirectToStreamsPage();
    close();
  };

  const onChangeVideoStreamClick = () => {
    AnalyticsService.event(EventNameAMP.ADDRESS_VIDEO_STREAM_ADD_CLICKED, {
      [ParamNameAMP.TYPE]: 'success bind device modal',
    });

    setFlowType(SelectPlayerFlowType.FULL_ONBOARDING);
    redirectToVideoStreamsPage();
    close();
  };

  useEffect(() => {
    let successModalType = StreamBindSuccessModalType.COMPLETED;

    if (showWebPlayerButton) {
      successModalType = StreamBindSuccessModalType.OPEN_WEBPLAYER;
    }

    if (!addressHasStream) {
      successModalType = StreamBindSuccessModalType.SELECT_WAVE;
    }

    AnalyticsService.event(StreamBindFlowEventNameAMP.DEVICE_BIND_SUCCESS_MODAL_SHOWED, {
      [StreamBindFlowParamNameAMP.SUCCESS_MODAL_TYPE]: successModalType,
    });

    if (successModalType !== StreamBindSuccessModalType.SELECT_WAVE) {
      AnalyticsService.eventGTM(EventNameGTM.WAVE_INITIATION_VIEWED);
    }
  }, []);

  return {
    addressHasStream,
    addressHasVideoStream,
    showWebPlayerButton,
    onOpenWebPlayerClick,
    onChangeStreamClick,
    onChangeVideoStreamClick,
    onClose,
    onBack,
  };
};

export default useDeviceBindSuccessModalViewModel;
